import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './StrategyDetail.module.css';
import btStyles from './BacktestPage.module.css';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import Spinner from '../../components/common/Spinner/Spinner';
import SignalCard from '../../components/strategies/SignalCard/SignalCard';
// Backtest now lives on its own page (`/strategies/:id/backtest`) so
// the matches table has full-width room as the indicator-column
// set grows. The old BacktestModal file is kept on disk unused
// for now in case we ever need to roll back.
import { useStrategies } from '../../hooks/useStrategies';
import { useSignals } from '../../hooks/useSignals';
import { toast } from 'react-toastify';
import { parseTrailingStopLossConfig, parseTargetConfig } from './strategyHelpers';
import { formatISTDate, formatISTDateTime } from '../../utils/datetime';

const StrategyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedStrategy, loading, fetchStrategyById, toggleStrategy, deleteStrategy } = useStrategies();
  const { loading: signalsLoading, fetchSignalsByStrategy } = useSignals();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [symbolsOpen, setSymbolsOpen] = useState(false);
  const [signalPage, setSignalPage] = useState(1);
  const signalLimit = 10;
  const [strategySignals, setStrategySignals] = useState([]);

  // Brokerage & taxes is edited inside the strategy form (Step 2,
  // below Target / Take Profit) along with the other trade
  // economics — no state or handlers for it here anymore.

  // Trailing-stop-loss is a strategy RULE (sibling of exit
  // conditions), not config metadata. Editing happens inside
  // the strategy form (Step 2, just below Exit Conditions), not
  // here on the detail page. This page only displays the current
  // TSL value read-only — no state, no handlers needed.

  // ── Backtest config card state ──────────────────────────
  // Mirrors the config card from BacktestPage. All state is
  // local UI-only — no API calls happen here. When the user
  // clicks "Run Backtest", we navigate to the BacktestPage
  // passing the selected values via React Router's location.state.
  const [btStartDate, setBtStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [btEndDate, setBtEndDate] = useState(
    () => new Date().toISOString().split('T')[0]
  );
  const [btSymbol, setBtSymbol] = useState(null);

  const btAvailableSymbols = useMemo(() => {
    if (!selectedStrategy) return [];
    if (Array.isArray(selectedStrategy.symbols) && selectedStrategy.symbols.length > 0) {
      return selectedStrategy.symbols;
    }
    return selectedStrategy.symbol ? [selectedStrategy.symbol] : [];
  }, [selectedStrategy]);

  // Auto-select the first symbol when the strategy loads.
  // Re-picks if the user's selection disappears (strategy
  // edited in another tab).
  useEffect(() => {
    if (btAvailableSymbols.length > 0 && (!btSymbol || !btAvailableSymbols.includes(btSymbol))) {
      setBtSymbol(btAvailableSymbols[0]);
    }
  }, [btAvailableSymbols, btSymbol]);

  const btDateError = useMemo(() => {
    if (!btStartDate || !btEndDate) return 'Both dates are required';
    if (new Date(btStartDate) > new Date(btEndDate))
      return 'Start must be on or before end';
    const diff =
      (new Date(btEndDate) - new Date(btStartDate)) / (1000 * 60 * 60 * 24);
    if (diff > 30) return 'Max range is 30 days';
    return '';
  }, [btStartDate, btEndDate]);

  const handleRunBacktest = useCallback(() => {
    if (btDateError || !btSymbol) return;
    navigate(`/strategies/${id}/backtest`, {
      state: { symbol: btSymbol, startDate: btStartDate, endDate: btEndDate, autoRun: true }
    });
  }, [id, btSymbol, btStartDate, btEndDate, btDateError, navigate]);

  useEffect(() => {
    if (id) {
      fetchStrategyById(id).catch(() => {});
    }
  }, [id, fetchStrategyById]);

  useEffect(() => {
    if (id) {
      fetchSignalsByStrategy(id, signalPage, signalLimit).then(res => {
        if (res?.data) setStrategySignals(res.data);
      }).catch(() => {});
    }
  }, [id, signalPage, fetchSignalsByStrategy]);

  const handleToggle = async () => {
    try {
      await toggleStrategy(id);
      toast.success(selectedStrategy?.is_active ? 'Strategy paused' : 'Strategy activated');
      fetchStrategyById(id);
    } catch (err) {
      toast.error(err.message || 'Failed to toggle');
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteStrategy(id);
      toast.success('Strategy deleted');
      navigate('/strategies');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const renderCondition = (cond, i, total) => {
    const value = cond.valueType === 'IndicatorValue' ? cond.value : cond.value;
    return (
      <div key={i} className={styles.condLine}>
        <span className={styles.condPill}>
          {cond.variable} {cond.operator} {value}
        </span>
        {i < total - 1 && (
          <span className={styles.condLogic}>{cond.logicalOperator || 'AND'}</span>
        )}
      </div>
    );
  };

  if (loading && !selectedStrategy) {
    return (
      <div className={styles.page}>
        <div className={styles.spinnerCenter}><Spinner /></div>
      </div>
    );
  }

  if (!selectedStrategy) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--text-muted)' }}>search_off</span>
          <h3>Strategy not found</h3>
          <Button variant="outline" onClick={() => navigate('/strategies')}>Back to Strategies</Button>
        </div>
      </div>
    );
  }

  const strategy = selectedStrategy;
  const conditions = strategy.conditions || [];
  const exitConditions = strategy.exit_conditions || [];
  const tslCfg = parseTrailingStopLossConfig(strategy.trailing_stop_loss);
  const targetCfg = parseTargetConfig(strategy.target);

  // Per-symbol state list for the Symbols section. The backend's
  // findById returns this as a rich array of objects from the
  // strategy_symbols junction. If we ever get a response without
  // it (older deploy, in-flight cache) we fall back to deriving a
  // best-effort list from the plain `symbols` string array — all
  // entries default to IDLE so we don't lie about trigger state.
  const symbolStates = Array.isArray(strategy.symbol_states) && strategy.symbol_states.length > 0
    ? strategy.symbol_states
    : (Array.isArray(strategy.symbols)
        ? strategy.symbols.map(sym => ({
            symbol: sym,
            trigger_state: 'IDLE',
            trigger_count: 0,
            last_triggered_at: null,
          }))
        : (strategy.symbol
            ? [{
                symbol: strategy.symbol,
                trigger_state: 'IDLE',
                trigger_count: 0,
                last_triggered_at: null,
              }]
            : []));

  return (
    <div className={`${styles.page} ${styles.pageEnter}`}>
      {/* Header — single horizontal row:
            [← Back]  testing-6 (badges below)   [Backtest][Pause][Delete]
          The back button is the left-most element, the title block
          sits next to it, and the action buttons are pushed to the
          right end via justify-content: space-between on .header. */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate('/strategies')}>
            <span className="material-symbols-outlined">arrow_back</span>
            Back
          </button>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{strategy.name}</h1>
            <div className={styles.badges}>
              <span className={`${styles.statusBadge} ${strategy.is_active ? styles.statusActive : styles.statusInactive}`}>
                <span className={styles.statusDot} />
                {strategy.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className={`${styles.actionBadge} ${strategy.action === 'BUY' ? styles.buyBadge : styles.sellBadge}`}>
                {strategy.action}
              </span>
              <span className={styles.actionBadge} style={{ background: 'var(--background-gray)', color: 'var(--text-secondary)' }}>
                {strategy.timeframe || '1m'}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button variant={strategy.is_active ? 'outline' : 'primary'} onClick={handleToggle} loading={loading}>
            {strategy.is_active ? 'Pause' : 'Activate'}
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Backtest config card — Symbol, Start Date, End Date, Run.
          All state is local UI; clicking Run navigates to the
          BacktestPage with the values pre-filled via location.state.
          Uses BacktestPage's CSS module (btStyles) for identical
          styling — no CSS duplication. */}
      <div className={styles.backtestSection}>
        <div className={btStyles.configCard}>
          <div className={styles.backtestHeader}>
            <h2 className={styles.sectionTitle}>Backtest</h2>
          </div>
          <div className={btStyles.dateRow}>
            {btAvailableSymbols.length > 0 && (
              <div className={btStyles.symbolField}>
                <label className={btStyles.label} htmlFor="bt-detail-symbol">Symbol</label>
                {btAvailableSymbols.length > 1 ? (
                  <select
                    id="bt-detail-symbol"
                    className={btStyles.select}
                    value={btSymbol || ''}
                    onChange={(e) => setBtSymbol(e.target.value)}
                  >
                    {btAvailableSymbols.map((sym) => (
                      <option key={sym} value={sym}>{sym}</option>
                    ))}
                  </select>
                ) : (
                  <div className={btStyles.symbolStaticText}>{btSymbol || '—'}</div>
                )}
              </div>
            )}
            <div className={btStyles.dateField}>
              <label className={btStyles.label}>Start Date</label>
              <input
                type="date"
                className={btStyles.input}
                value={btStartDate}
                max={btEndDate}
                onChange={(e) => setBtStartDate(e.target.value)}
              />
            </div>
            <div className={btStyles.dateField}>
              <label className={btStyles.label}>End Date</label>
              <input
                type="date"
                className={btStyles.input}
                value={btEndDate}
                min={btStartDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setBtEndDate(e.target.value)}
              />
            </div>
            <div className={btStyles.runField}>
              <Button
                variant="primary"
                onClick={handleRunBacktest}
                disabled={!!btDateError || !btSymbol}
              >
                Run Backtest
              </Button>
            </div>
          </div>
          {btDateError && <p className={btStyles.errorText}>{btDateError}</p>}
          {btAvailableSymbols.length === 0 && (
            <p className={btStyles.errorText}>
              This strategy has no symbols configured. Add at least one symbol before backtesting.
            </p>
          )}
        </div>
      </div>

      {/* Brokerage & Taxes moved into the strategy form (Step 2,
          below Target / Take Profit) — edit the strategy to change
          cost percentages. */}

      {/* Conditions — three columns: Entry | Exit | Risk Management */}
      <div className={styles.conditionsGrid}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Entry Conditions</h2>
          {conditions.length === 0 ? (
            <p className={styles.emptyText}>No entry conditions defined.</p>
          ) : (
            <div className={styles.condList}>
              {conditions.map((cond, i) => renderCondition(cond, i, conditions.length))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Exit Conditions</h2>
          {exitConditions.length === 0 ? (
            <p className={styles.emptyText}>No exit conditions defined.</p>
          ) : (
            <div className={styles.condList}>
              {exitConditions.map((cond, i) => renderCondition(cond, i, exitConditions.length))}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Risk Management</h2>
          {!tslCfg && !targetCfg ? (
            <p className={styles.emptyText}>No stop loss or target configured.</p>
          ) : (
            <div className={styles.condList}>
              {tslCfg && (
                <div className={styles.condLine}>
                  <span className={styles.condPill}>
                    {tslCfg.type === 'fixed'
                      ? `Fixed Stop Loss: ${tslCfg.value}% from entry`
                      : tslCfg.type === 'percent'
                        ? `Trailing Stop Loss: ${tslCfg.value}% from peak`
                        : `TSL: SL ${tslCfg.initial_stop_pct}% · Hurdle ${tslCfg.hurdle_pct}% · Step ${tslCfg.step_pct}%`}
                  </span>
                </div>
              )}
              {targetCfg && (
                <div className={styles.condLine}>
                  <span className={styles.condPill}>
                    {targetCfg.type === 'percent'
                      ? `Target: ${targetCfg.value}% from entry`
                      : `Target: ₹${Number(targetCfg.value).toLocaleString('en-IN')} (fixed price)`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Symbols section — collapsible dropdown, closed by default.
          Clicking the header row toggles the chip list open/closed. */}
      <div className={`${styles.symbolsSection} ${symbolsOpen ? styles.symbolsOpen : ''}`}>
        <button
          type="button"
          className={styles.symbolsToggle}
          onClick={() => setSymbolsOpen(prev => !prev)}
          aria-expanded={symbolsOpen}
        >
          <div className={styles.symbolsToggleLeft}>
            <h2 className={styles.sectionTitle}>Symbols</h2>
            <span className={styles.symbolsCountBadge}>{symbolStates.length}</span>
          </div>
          <div className={styles.symbolsToggleRight}>
            <span className={styles.symbolsCreatedDate}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>calendar_today</span>
              Created {formatISTDate(strategy.created_at)}
            </span>
            <span className={`material-symbols-outlined ${styles.symbolsChevron}`}>
              expand_more
            </span>
          </div>
        </button>
        {symbolsOpen && (
          <div className={styles.symbolsBody}>
            {symbolStates.length === 0 ? (
              <p className={styles.emptyText}>No symbols configured.</p>
            ) : (
              <div className={styles.symbolStatesList}>
                {symbolStates.map((s) => {
                  const isTriggered = s.trigger_state === 'TRIGGERED';
                  const tooltipParts = [
                    `${s.symbol} — ${s.trigger_state}`,
                  ];
                  if (s.trigger_count) tooltipParts.push(`Fired ${s.trigger_count}×`);
                  if (s.last_triggered_at) {
                    tooltipParts.push(`Last: ${formatISTDateTime(s.last_triggered_at)}`);
                  }
                  return (
                    <div
                      key={s.symbol}
                      className={`${styles.symbolStateChip} ${isTriggered ? styles.symbolTriggered : styles.symbolIdle}`}
                      title={tooltipParts.join(' • ')}
                    >
                      <span className={styles.symbolStateDot} />
                      <span className={styles.symbolStateName}>{s.symbol}</span>
                      <span className={styles.symbolStateLabel}>{s.trigger_state}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Signal History */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Signal History</h2>
        {signalsLoading ? (
          <div className={styles.spinnerCenter}><Spinner /></div>
        ) : strategySignals.length === 0 ? (
          <p className={styles.emptyText}>No signals generated yet.</p>
        ) : (
          <div className={styles.signalList}>
            {strategySignals.map(signal => (
              <SignalCard key={signal.id} signal={signal} />
            ))}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="small"
        actions={
          <>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} loading={loading}>Delete Strategy</Button>
          </>
        }
      >
        <p>Are you sure you want to delete <strong>{strategy.name}</strong>?</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginTop: 8 }}>
          This action cannot be undone. Signal history will be preserved.
        </p>
      </Modal>

      {/* Backtest moved to /strategies/:id/backtest — see button in header */}
    </div>
  );
};

export default StrategyDetail;
